/**
 * Weekly Report Generator - Chatbot-Powered Reports
 * Uses the accurate meal-data-service with Groq AI
 * Prevents hallucinations by using real daily data
 */

import React, { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { apiClient } from '../../config/api';

interface WeeklyReportGeneratorProps {
  user: any;
  onBack: () => void;
  coachMode?: boolean;
  clientId?: number;
}

type SophisticationLevel = 'beginner' | 'intermediate' | 'advanced';

interface WeeklyReport {
  reportId: string;
  reportText: string;
  period: {
    start: string;
    end: string;
    daysLogged: number;
  };
  data: {
    weeklyAverage: {
      calories: number;
      protein: number;
      carbs: number;
      fat: number;
      fiber: number;
      daysLogged: number;
      mealsPerDay: string;
    };
    deficiencies: Array<{
      nutrient: string;
      current: number;
      target: number;
      percentage: number;
      severity: string;
    }>;
  };
  sophisticationLevel: string;
  generatedAt: string;
}

export const WeeklyReportGenerator: React.FC<WeeklyReportGeneratorProps> = ({ user, onBack, coachMode, clientId }) => {
  const [sophisticationLevel, setSophisticationLevel] = useState<SophisticationLevel>('intermediate');
  const [generatedReport, setGeneratedReport] = useState<WeeklyReport | null>(null);

  // Determine which user ID to use
  const targetUserId = coachMode && clientId ? clientId : user?.id;

  // Fetch past reports
  const { data: pastReports, refetch: refetchReports } = useQuery({
    queryKey: coachMode ? ['weekly-reports', 'client', clientId] : ['weekly-reports', user?.id],
    queryFn: async () => {
      const url = coachMode && clientId
        ? `${process.env.REACT_APP_API_URL}/api/reports/my-reports?userId=${clientId}`
        : `${process.env.REACT_APP_API_URL}/api/reports/my-reports`;

      const response = await fetch(url, {
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Failed to fetch reports');
      const data = await response.json();
      return data.reports;
    },
    enabled: coachMode ? !!clientId : !!user?.id
  });

  // Generate new report mutation
  const generateMutation = useMutation({
    mutationFn: async (level: SophisticationLevel) => {
      const requestBody: any = {
        sophisticationLevel: level,
        emailReport: false
      };

      // If coach mode, include target user ID
      if (coachMode && clientId) {
        requestBody.userId = clientId;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/reports/generate-weekly`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to generate report');
      }

      const data = await response.json();
      return data.report;
    },
    onSuccess: (report) => {
      setGeneratedReport(report);
      refetchReports();
    }
  });

  const handleGenerate = () => {
    generateMutation.mutate(sophisticationLevel);
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium mb-2"
          >
            ← Back to Reports
          </button>
          <h2 className="text-2xl font-bold text-gray-900">Weekly Report Generator</h2>
          <p className="text-gray-600 mt-1">AI-powered weekly nutrition insights using your real data</p>
        </div>
      </div>

      {/* Generator Card */}
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-medium text-gray-900">Generate New Report</h3>
        </div>
        <div className="card-body">
          <div className="space-y-4">
            {/* Sophistication Level Selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Report Style
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <button
                  onClick={() => setSophisticationLevel('beginner')}
                  className={`p-4 border-2 rounded-lg text-left transition-all ${
                    sophisticationLevel === 'beginner'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="text-lg font-medium text-gray-900 mb-1">
                    🌱 Beginner
                  </div>
                  <div className="text-sm text-gray-600">
                    Simple, encouraging language. Focus on quick wins and actionable tips.
                  </div>
                </button>

                <button
                  onClick={() => setSophisticationLevel('intermediate')}
                  className={`p-4 border-2 rounded-lg text-left transition-all ${
                    sophisticationLevel === 'intermediate'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="text-lg font-medium text-gray-900 mb-1">
                    📊 Intermediate
                  </div>
                  <div className="text-sm text-gray-600">
                    Data-informed insights with trends and patterns. Balanced detail.
                  </div>
                </button>

                <button
                  onClick={() => setSophisticationLevel('advanced')}
                  className={`p-4 border-2 rounded-lg text-left transition-all ${
                    sophisticationLevel === 'advanced'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="text-lg font-medium text-gray-900 mb-1">
                    🎓 Advanced
                  </div>
                  <div className="text-sm text-gray-600">
                    Comprehensive analysis with scientific context and detailed metrics.
                  </div>
                </button>
              </div>
            </div>

            {/* Generate Button */}
            <div className="flex justify-center pt-4">
              <button
                onClick={handleGenerate}
                disabled={generateMutation.isPending}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                {generateMutation.isPending ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Generating Report...
                  </span>
                ) : (
                  '✨ Generate Weekly Report'
                )}
              </button>
            </div>

            {/* Error Display */}
            {generateMutation.isError && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="text-red-800 font-medium">Error Generating Report</div>
                <div className="text-red-600 text-sm mt-1">
                  {generateMutation.error instanceof Error ? generateMutation.error.message : 'Unknown error occurred'}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Generated Report Display */}
      {generatedReport && (
        <div className="card">
          <div className="card-header">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Your Weekly Report</h3>
                <p className="text-sm text-gray-600 mt-1">
                  {formatDate(generatedReport.period.start)} - {formatDate(generatedReport.period.end)} • {generatedReport.period.daysLogged} days logged
                </p>
              </div>
              <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                {generatedReport.sophisticationLevel}
              </span>
            </div>
          </div>
          <div className="card-body">
            {/* Weekly Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg mb-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">
                  {generatedReport.data.weeklyAverage.calories}
                </div>
                <div className="text-sm text-gray-600">Avg Calories/Day</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {generatedReport.data.weeklyAverage.protein}g
                </div>
                <div className="text-sm text-gray-600">Avg Protein/Day</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">
                  {generatedReport.period.daysLogged}/7
                </div>
                <div className="text-sm text-gray-600">Days Logged</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-orange-600">
                  {generatedReport.data.weeklyAverage.mealsPerDay}
                </div>
                <div className="text-sm text-gray-600">Meals/Day</div>
              </div>
            </div>

            {/* Deficiencies Alert */}
            {generatedReport.data.deficiencies && generatedReport.data.deficiencies.length > 0 && (
              <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <h4 className="font-medium text-yellow-900 mb-2">📊 Nutrient Gaps Identified</h4>
                <div className="space-y-2">
                  {generatedReport.data.deficiencies.slice(0, 3).map((def, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <span className="text-yellow-800">
                        {def.nutrient}: <strong>{def.current}</strong> vs {def.target} target
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        def.severity === 'critical' ? 'bg-red-100 text-red-800' :
                        def.severity === 'moderate' ? 'bg-orange-100 text-orange-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {def.percentage}% • {def.severity}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* AI-Generated Report Text */}
            <div className="prose max-w-none">
              <div className="whitespace-pre-wrap text-gray-700 leading-relaxed">
                {generatedReport.reportText}
              </div>
            </div>

            {/* Footer */}
            <div className="mt-6 pt-4 border-t border-gray-200 text-xs text-gray-500">
              Generated {new Date(generatedReport.generatedAt).toLocaleString()} •
              Uses real data from your meal tracking (no hallucinations!) •
              Powered by Groq AI
            </div>
          </div>
        </div>
      )}

      {/* Past Reports */}
      {pastReports && pastReports.length > 0 && (
        <div className="card">
          <div className="card-header">
            <h3 className="text-lg font-medium text-gray-900">Past Reports</h3>
          </div>
          <div className="card-body">
            <div className="space-y-3">
              {pastReports.map((report: any) => (
                <div
                  key={report.id}
                  className="p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => {
                    // Load this report
                    setGeneratedReport({
                      reportId: report.id,
                      reportText: report.report_text,
                      period: {
                        start: report.period_start,
                        end: report.period_end,
                        daysLogged: report.days_logged
                      },
                      data: { weeklyAverage: {}, deficiencies: [] } as any, // Simplified for past reports
                      sophisticationLevel: report.sophistication_level,
                      generatedAt: report.generated_at
                    });
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-gray-900">
                        {formatDate(report.period_start)} - {formatDate(report.period_end)}
                      </div>
                      <div className="text-sm text-gray-600 mt-1">
                        {report.days_logged} days • {report.sophistication_level} style
                      </div>
                    </div>
                    <div className="text-sm text-gray-500">
                      {new Date(report.generated_at).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
